import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import ReusableForm from "../common/ReusableForm";
import ReusableTable from "../dashboard/ReusableTable";
import { getRequest, postRequest } from "../services/apiService"; // Only postRequest needed now
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';

const ShipActionCell = ({ rowData, onEdit, onDelete }) => {
  const op = useRef(null);
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text p-2" // Added p-2 for padding
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

const ShipDialog = ({ visible, onHide, projectData, fetchProjects, toast, isModalOverlay }) => {
  const [ships, setShips] = useState([]);
  const [shipsFirst, setShipsFirst] = useState(0);
  const [shipsRows, setShipsRows] = useState(5);
  const [shipsTotalRecords, setShipsTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingShip, setEditingShip] = useState(null);
  const [shipyards, setShipyards] = useState([]);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shipToDelete, setShipToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef(null);

  // --- Fullscreen logic must be defined before JSX usage ---
  const toggleFullscreen = () => {
    const elem = modalRef.current;
    if (!elem) return;
    if (!document.fullscreenElement && !document.mozFullScreenElement && !document.webkitFullscreenElement && !document.msFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!(document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('mozfullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    document.addEventListener('msfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('mozfullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
      document.removeEventListener('msfullscreenchange', onFullscreenChange);
    };
  }, []);

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [formData, setFormData] = useState({
    name: "",
    start_date: "",
    end_date: "",
    yard: "",
    status_db: 1,
    parent: projectData?.id || 1,
    budget: 0,
    shipyard: "" // Added shipyard to form data for edit
  });
  useEffect(() => {
    const fetchShipyards = async () => {
      try {
        const response = await getRequest("/master/shipyards/");
        setShipyards(response.data);
      } catch (error) {
        console.error("Error fetching shipyards:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load shipyards",
          life: 3000
        });
      }
    };

    if (visible) fetchShipyards();
  }, [visible]);

  const shipFormFields = [
    { name: "name", label: "Ship Name", type: "text", placeholder: "Enter ship name", required: true },
    { name: "start_date", label: "Start Date", type: "date", required: true },
    { name: "end_date", label: "End Date", type: "date", required: true },
    { name: "yard", label: "Yard", type: "text", placeholder: "Enter yard", required: true },
    {
      name: "shipyard",
      label: "Shipyard",
      type: "select",
      options: shipyards.map(shipyard => ({
        label: shipyard.name,
        value: shipyard.id
      })),
      required: true
    }
    
  ];

  const columns = [
    { field: "name", header: "Ship Name", sortable: true },
    { field: "start_date", header: "Start Date", sortable: true },
    { field: "end_date", header: "End Date", sortable: true },
    { field: "yard", header: "Yard", sortable: true },
    // { field: "code", header: "Ship Code", sortable: true },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => (
        <ShipActionCell rowData={rowData} onEdit={handleEditShip} onDelete={handleDeleteShip} />
      ),
      notFilterable: true
    }
  ];

  // Fetch ships for the current project
  const fetchShips = async () => {
    if (!projectData?.id) return;
    setLoading(true);
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectData.id}&search=${encodeURIComponent(search)}`);
      let shipsData = [];
      if (response?.results?.data) shipsData = response.results.data;
      else if (response?.data) shipsData = response.data;
      else if (Array.isArray(response)) shipsData = response;
      setShips(shipsData);
      setShipsTotalRecords(shipsData.length);
    } catch (error) {
      // handle error if needed
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect for ships
  useEffect(() => {
    if (!visible || !projectData?.id) return;
    fetchShips();
    // eslint-disable-next-line
  }, [visible, projectData, search]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  useEffect(() => {
    if (visible) {
      resetForm();
    }
  }, [visible, projectData]);

  const resetForm = () => {
    setFormData({
      name: "",
      start_date: "",
      end_date: "",
      yard: "",
      status_db: 1,
      parent: projectData?.id || 1,
      budget: 0,
      shipyard: "" // Reset shipyard to default value
    });
    setEditingShip(null);
  };

  const handleSaveShip = async () => {
    const requiredFields = ["name", "start_date", "end_date", "yard"];
    for (let field of requiredFields) {
      if (!formData[field] && formData[field] !== 0) {
        toast.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: `${field.replace('_', ' ')} is required`,
          life: 3000
        });
        return;
      }
    }

    const payload = {
      ...formData,
      budget: 0, // Explicitly set budget to 0 to satisfy the API requirement
      status_db: 1,
      parent: projectData?.id || 1
    };

    setLoading(true);
    try {
      if (editingShip) {
        // UPDATE: Use POST with id in payload instead of PUT
        await postRequest(`/master/projects/`, { ...payload, id: editingShip.id });
        toast.current?.show({ severity: "success", summary: "Success", detail: "Ship updated successfully", life: 3000 });
        await fetchShips();
      } else {
        await postRequest("/master/projects/", payload);
        toast.current?.show({ severity: "success", summary: "Success", detail: "Ship added successfully", life: 3000 });
        await fetchShips();
      }
      resetForm();
    } catch (error) {
      console.error("Error saving ship:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${editingShip ? "update" : "add"} ship`,
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditShip = (ship) => {
    setEditingShip(ship);
    setFormData({
      name: ship.name || "",
      start_date: ship.start_date || "",
      end_date: ship.end_date || "",
      yard: ship.yard || "",
      status_db: 1,
      parent: ship.parent?.id || projectData?.id || 1,
      budget: ship.budget || 0,
      shipyard: ship.shipyard?.id || ""
    });
    setShowEditDialog(true);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingShip(null);
    resetForm();
  };

  const handleDeleteShip = (ship) => {
    setShipToDelete(ship);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteDialog(true);
  };

  const confirmDeleteShip = async () => {
    if (deleteFormData.confirmation !== 'DELETE') {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }
    try {
      // DELETE: Use POST with status_db=3 instead of DELETE method
      await postRequest(`/master/projects/`, { 
        status_db: 3,
        id: shipToDelete.id 
      });
      toast.current?.show({ severity: "success", summary: "Success", detail: "Ship deleted successfully", life: 3000 });
      setShowDeleteDialog(false);
      setShipToDelete(null);
      setDeleteFormData({ confirmation: '' });
      await fetchShips();
    } catch (error) {
      console.error("Error deleting ship:", error);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to delete ship", life: 3000 });
    }
  };

  // Update shipsTotalRecords if ships change
  useEffect(() => {
    setShipsTotalRecords(ships.length);
  }, [ships]);

  // Handle page change for ships
  const onShipsPageChange = (event) => {
    setShipsFirst(event.first);
    setShipsRows(event.rows);
  };

  const mainDialogContent = (
    <div className="p-fluid">
      <ReusableForm
        formData={formData}
        setFormData={setFormData}
        fields={shipFormFields}
        onSubmit={handleSaveShip} // This will be the add form
        loading={loading}
        submitLabel="Add Ship"
        buttonClassName="px-2 py-2 text-xs rounded-md"
        onHide={onHide}
      />

      <div className="mt-4">
        <ReusableTable
          data={ships.slice(shipsFirst, shipsFirst + shipsRows)}
          columnFields={columns}
          loading={loading}
          rows={shipsRows}
          totalRecords={shipsTotalRecords}
          first={shipsFirst}
          onPageChange={onShipsPageChange}
          emptyMessage="No ships found for this project"
          hideSearchAndExport={true}
        />
      </div>

      <ReusableDeleteDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onDelete={confirmDeleteShip}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Ship"
        description={`Are you sure you want to delete ship "${shipToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );

  // Conditional rendering for Edit Ship form using ReusableForm modal pattern
  if (showEditDialog) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-40"> {/* Higher z-index for edit modal */}
        <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
          <ReusableForm
            formData={formData} // formData is already set by handleEditShip
            setFormData={setFormData}
            fields={shipFormFields} // Assuming shipFormFields are suitable for edit too
            onSubmit={async () => {
              await handleSaveShip(); // handleSaveShip handles both add and edit based on editingShip state
              closeEditDialog();
            }}
            onHide={closeEditDialog}
            loading={loading}
            headerText={`Edit Ship - ${editingShip?.name || ""}`}
            submitLabel="Update Ship"
          />
        </div>
      </div>
    );
  }

  // If isModalOverlay is true, wrap in modal structure. Otherwise, render as is (though this case might become unused).
  if (isModalOverlay) {
    return (
      <div ref={modalRef} className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col h-[90vh] max-h-[800px]">
        <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
          <h3 className="text-xl font-bold text-white tracking-wide">Manage Ships for: {projectData?.name || "Project"}</h3>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              icon={isFullscreen ? "pi pi-window-minimize" : "pi pi-window-maximize"}
              onClick={toggleFullscreen}
              className="p-button-text p-button-secondary text-white"
              tooltip="Toggle Fullscreen"
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded text-white"
              onClick={() => {
                resetForm();
                onHide();
              }}
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-grow p-6">
          {mainDialogContent}
        </div>
      </div>
    );
  }

  // Fallback for non-modal rendering (might be deprecated by new approach)
  return (
    <Dialog
      visible={visible}
      headerClassName="bg-[#1e3a8a] text-white px-6 py-4 rounded-t-lg"
      header={
        <div className="flex justify-between items-center">
          <span>Manage Ships for {projectData?.name || "Project"}</span>
        </div>
      }
      onHide={() => {
        resetForm();
        onHide();
      }}
      style={{ width: "1000px" }}
      modal
    >
      {mainDialogContent}
    </Dialog>
  );
};

export default ShipDialog;